import { useState } from 'react';
import { Package, Save } from 'lucide-react';
import OpsModal from '@/components/ops/OpsModal';

export default function PackageModal({ package: existing, onClose, onSave }) {
  const isEdit = !!existing;
  const [form, setForm] = useState({
    stripe_price_id: existing?.stripe_price_id || '',
    package_name:    existing?.package_name || '',
    description:     existing?.description || '',
    display_order:   existing?.display_order ?? 99,
    is_active:       existing?.is_active ?? true,
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    setError('');
    if (!form.stripe_price_id.trim() || !form.package_name.trim()) {
      setError('Stripe Price ID and Package name are required.');
      return;
    }
    setSaving(true);
    try {
      await onSave(form);
      onClose();
    } catch (e) {
      setError(e?.response?.data?.detail || 'Could not save the package.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <OpsModal
      title={isEdit ? 'Edit package' : 'Add package'}
      subtitle={isEdit ? 'Update name, description, order, or activation. Amount refreshes from Stripe on save.' : 'Map a Stripe Price ID to a Heirloom package. Amount is fetched from Stripe.'}
      icon={Package}
      onClose={onClose}
      size="md"
      testid="package-modal"
      footer={
        <div className="flex items-center justify-end gap-2">
          <button onClick={onClose} className="h-9 text-[13px] text-slate-700 hover:text-slate-900 hover:bg-slate-100 border border-slate-200 rounded-md px-3 transition-colors">Cancel</button>
          <button
            onClick={handleSave}
            disabled={saving}
            data-testid="package-modal-save"
            className="h-9 bg-slate-900 hover:bg-slate-800 text-white px-4 rounded-md text-[13px] font-medium shadow-sm disabled:opacity-50 transition-colors inline-flex items-center gap-1.5"
          >
            <Save size={13} /> {saving ? 'Saving…' : isEdit ? 'Save changes' : 'Add package'}
          </button>
        </div>
      }
    >
      <div className="space-y-3.5 p-1">
        <Field label="Stripe Price ID" testid="package-price-id">
          <input
            value={form.stripe_price_id}
            onChange={(e) => setForm(f => ({ ...f, stripe_price_id: e.target.value }))}
            disabled={isEdit}
            placeholder="price_1..."
            data-testid="package-price-id-input"
            className="w-full h-9 border border-slate-200 rounded-md px-2.5 text-[13px] font-mono focus:border-slate-400 focus:outline-none disabled:bg-slate-50 disabled:text-slate-500"
          />
          {isEdit && <p className="text-[11px] text-slate-500 mt-1">Price ID can't be changed — delete and re-add if you need to switch.</p>}
        </Field>
        <Field label="Package name">
          <input
            value={form.package_name}
            onChange={(e) => setForm(f => ({ ...f, package_name: e.target.value }))}
            placeholder="Heirloom Archive Collection"
            data-testid="package-name-input"
            className="w-full h-9 border border-slate-200 rounded-md px-2.5 text-[13px] focus:border-slate-400 focus:outline-none"
          />
        </Field>
        <Field label="Description (shown on Buy Now card)">
          <textarea
            value={form.description}
            onChange={(e) => setForm(f => ({ ...f, description: e.target.value }))}
            rows={3}
            placeholder="A short, warm sentence about what this package includes."
            data-testid="package-description-input"
            className="w-full border border-slate-200 rounded-md px-2.5 py-2 text-[13px] focus:border-slate-400 focus:outline-none resize-none"
          />
        </Field>
        <div className="grid grid-cols-2 gap-3">
          <Field label="Display order">
            <input
              type="number"
              value={form.display_order}
              onChange={(e) => setForm(f => ({ ...f, display_order: e.target.value }))}
              data-testid="package-order-input"
              className="w-full h-9 border border-slate-200 rounded-md px-2.5 text-[13px] focus:border-slate-400 focus:outline-none"
            />
          </Field>
          <Field label="Active">
            <label className="h-9 inline-flex items-center gap-2 text-[13px] text-slate-700 cursor-pointer">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => setForm(f => ({ ...f, is_active: e.target.checked }))}
                data-testid="package-active-input"
                className="h-4 w-4 rounded border-slate-300 text-slate-900 focus:ring-1 focus:ring-slate-900 accent-slate-900"
              />
              Show on Buy Now panel
            </label>
          </Field>
        </div>
        {error && (
          <div className="text-[12px] text-red-700 bg-red-50 border border-red-200 rounded-md px-3 py-2" data-testid="package-modal-error">
            {error}
          </div>
        )}
      </div>
    </OpsModal>
  );
}

function Field({ label, children }) {
  return (
    <div>
      <label className="block text-[11px] uppercase tracking-wider font-medium text-slate-500 mb-1">{label}</label>
      {children}
    </div>
  );
}
